import React, { useState, useEffect, useRef } from 'react';

/**
 * CheckInUrlHandler component for entering 6-digit alphanumeric check-in codes
 *
 * @param {Object} props - Component props
 * @returns {JSX.Element} The manual code entry component
 */
const CheckInUrlHandler = () => {
  // Single state for the entire code
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showFaq, setShowFaq] = useState(false);

  // Create refs for each input field
  const inputRefs = useRef([]);
  inputRefs.current = Array(6).fill(null).map((_, i) => inputRefs.current[i] || React.createRef());

  const handleSubmit = (e) => {
    e.preventDefault();

    // Basic validation
    if (!code || code.length !== 6) {
      setError('Please enter all 6 characters of your check-in code');
      return;
    }

    // Check if it's a valid alphanumeric code (lowercase letters and numbers)
    if (!/^[a-z0-9]{6}$/.test(code)) {
      setError('Please enter a valid 6-character code (letters and numbers only)');
      return;
    }

    setIsLoading(true);
    setError('');

    // Construct the URL with the code and navigate to it
    const baseUrl = window.location.origin;
    const newUrl = `${baseUrl}/p-${code}`;

    // Navigate to the new URL which will trigger a page refresh
    window.location.href = newUrl;
  };

  const handlePaste = (e, index) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').toLowerCase().replace(/[^a-z0-9]/g, '');

    if (pastedData) {
      // Take up to 6 characters from the paste
      const newCode = pastedData.slice(0, 6);
      setCode(newCode);

      // Focus the next empty input or the last input
      const nextFocusIndex = Math.min(newCode.length, 5);
      if (inputRefs.current[nextFocusIndex]) {
        inputRefs.current[nextFocusIndex].current?.focus();
      }
    }
  };

  const handleInputChange = (value, index) => {
    // Convert to lowercase for consistency (backend generates lowercase codes)
    const val = value.toLowerCase().replace(/[^a-z0-9]/g, '');

    if (val.length <= 1) {
      // Update the code state by replacing the character at the given index
      const newCode = code.split('');
      newCode[index] = val;
      setCode(newCode.join('').slice(0, 6));

      // Auto-focus next input if a character was entered
      if (val.length === 1 && index < 5 && inputRefs.current[index + 1]) {
        inputRefs.current[index + 1].current?.focus();
      }
    }
  };

  const handleKeyDown = (e, index) => {
    // Handle backspace
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      e.preventDefault();
      // Move to previous input
      if (inputRefs.current[index - 1]) {
        inputRefs.current[index - 1].current?.focus();
      }
    }
  };

  // Initialize feather icons after component mounts
  useEffect(() => {
    if (typeof window.feather !== 'undefined') {
      window.feather.replace();
    }
  }, []);

  return (
    <div className="mt-3">
      <form onSubmit={handleSubmit}>
        <div className="form-group mb-3">
          <div className="custom-form-input-container">
            <input
              type="text"
              className="form-control custom-form-input prefix"
              defaultValue={'p'}
              disabled
              maxLength={1}
            />
            <input
              type="text"
              className="form-control custom-form-input prefix"
              defaultValue={'-'}
              disabled
              maxLength={1}
            />
            {[0, 1, 2, 3, 4, 5].map((index) => (
              <input
                key={index}
                ref={inputRefs.current[index]}
                type="text"
                className={`form-control custom-form-input`}
                style={{
                  textTransform: 'lowercase',
                  border: error ? '1px solid var(--bs-form-invalid-color)' : undefined
                }}
                id={`code-digit${index + 1}`}
                value={code[index] || ''}
                onChange={(e) => handleInputChange(e.target.value, index)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                onPaste={(e) => handlePaste(e, index)}
                maxLength={1}
                autoFocus={index === 0}
              />
            ))}
          </div>
        </div>
        {error && <div className="text-danger text-sm d-block mb-3">{error}</div>}
        <button type="submit" className="btn btn-primary mt-2" disabled={isLoading}>
          {isLoading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Loading...
            </>
          ) : (
            <span>Verify Code & Continue<i className="fas fa-rocket mx-2"></i></span>
          )}
        </button>
      </form>


      <button 
        className="btn btn-link mt-3 p-0 text-start text-decoration-none" 
        type="button" 
        onClick={() => setShowFaq(!showFaq)}
      >Where is the code? <i className={`fas fa-chevron-${showFaq ? 'up' : 'down'} ms-1`}></i>
      </button>
      {showFaq && (
        <div className="mb-3 p-3 mt-2 alert alert-light">
          Check your email or text for a message from Ankr (check spam folder too, just in case). Enter the 6-character code after <b>p-</b> from your check-in link.<br/> Example: If your link is <b>mira.ankr.us/p-abc123</b>, enter <b>abc123</b> above.
         </div>
      )}
    </div>
  );
};

export default CheckInUrlHandler;